// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { usePharmacy } from '../auth/PharmacyContext'
import { resolvePharmacyFromUrl, looksLikePharmacyCode } from '../auth/pharmacyUrl'

/**
 * Pharmacy workspace GATE — wraps the whole authenticated shell (AppShell), so the checks
 * below run on EVERY page, not just the home route. With the router mounted under a
 * basename of /p/{slug}, the slug stays in the URL for all navigation; this gate makes that
 * URL trustworthy.
 *
 * For a path-based pharmacy URL (/p/{slug}/...):
 *   - resolves the pharmacy from the slug and sets it as the active pharmacy for THIS tab
 *   - unauthenticated  → /p/{slug}/login   (basename prefixes the bare "/login")
 *   - unknown slug     → /p/{slug}/login
 *   - CROSS-TENANT: a session bound to a DIFFERENT pharmacy than the URL is bounced to
 *     re-auth for the URL's pharmacy. This is what stops a pasted /p/{slug} link from
 *     rendering another tenant's workspace in a browser logged into a different pharmacy.
 *     Platform/unbound users (no pharmacy_slug) legitimately view any pharmacy and are exempt.
 *
 * For apex / admin / subdomain URLs (no path slug) the gate is a passthrough — those contexts
 * are unchanged (subdomains carry the pharmacy via the host; admins manage the platform).
 */
export default function PharmacyWorkspaceEntry({ children }: { children: ReactNode }) {
  const { source, slug } = resolvePharmacyFromUrl()
  const location = useLocation()
  const isPathPharmacy = source === 'path' && !!slug
  const { me, loading } = useAuth()
  const { enterPharmacy } = usePharmacy()
  const [resolving, setResolving] = useState(isPathPharmacy)
  const [notFound, setNotFound] = useState(false)
  // The resolved pharmacy's real id — used for the cross-tenant check below so it works whether the
  // URL carries an opaque url_code or a legacy slug (the raw identifier can't be compared directly).
  const [resolvedId, setResolvedId] = useState<string | null>(null)

  useEffect(() => {
    if (!isPathPharmacy) { setResolving(false); return }
    if (loading) return
    if (!me?.authenticated) { setResolving(false); return }
    let cancelled = false
    void (async () => {
      try {
        // The /p/{id} identifier may be an opaque url_code (preferred) or a legacy slug. Resolve
        // by-code first when it looks like a code, then fall back to the slug endpoint (S6 retires slug).
        const fetchOne = async (url: string) => {
          const r = await fetch(url)
          if (r.ok) return await r.json()
          if (r.status === 404) return null
          throw new Error('branding_error')
        }
        let p: any = null
        if (looksLikePharmacyCode(slug!)) {
          p = await fetchOne(`/api/public/pharmacy-branding-by-code/${encodeURIComponent(slug!)}`)
        }
        if (!p) p = await fetchOne(`/api/public/pharmacy-branding/${encodeURIComponent(slug!)}`)
        if (cancelled) return
        if (!p?.id) { setNotFound(true); setResolving(false); return }

        // Canonical-URL normalization: if the URL carried a human-readable name-slug but the pharmacy
        // has an opaque url_code, redirect to /p/{url_code}/... so the name never persists in the
        // address bar (requirement: login/workspace URL must be a code, not the pharmacy name). This
        // is a full-page redirect because the router basename (/p/{slug}) is fixed at load. Skip when
        // the URL is already the code, or when no code exists (older rows).
        if (p.url_code && slug !== p.url_code && !looksLikePharmacyCode(slug!)) {
          const rest = window.location.pathname.replace(/^\/p\/[^/]+/, '') || '/'
          window.location.replace(`/p/${p.url_code}${rest}${window.location.search}${window.location.hash}`)
          return
        }

        setResolvedId(p.id)
        enterPharmacy({
          id: p.id,
          name: p.name,
          slug: p.slug || slug!,
          logo_url: p.logo_url || null,
          primary_color_hex: p.primary_color_hex || null,
        })
        setResolving(false)
      } catch {
        if (!cancelled) { setNotFound(true); setResolving(false) }
      }
    })()
    return () => { cancelled = true }
  }, [isPathPharmacy, loading, me?.authenticated, slug, enterPharmacy])

  // Apex deep-link recovery: a pharmacy-bound user who opened an OLDER apex email link (e.g.
  // /secure-share?doc=… with no /p/{slug}) has no pharmacy context at the apex host and would bounce.
  // Send them to the SAME route under their own /p/{slug} (full reload — the router basename is fixed
  // at load, so PharmacyWorkspaceEntry can re-resolve the pharmacy and load the workspace / branded
  // login). Scoped to the known workspace deep-link routes that appear in notification emails, so
  // platform/admin/public routes stay at apex.
  //
  // Remember the active pharmacy (the non-PHI slug/code from the URL) so that when the session later
  // lapses on an apex URL — or an OLDER apex email link is opened while logged out — we can route to
  // THIS pharmacy's BRANDED login instead of the public marketing page.
  useEffect(() => {
    if (isPathPharmacy && slug) { try { localStorage.setItem('owlrx_pharmacy_hint', slug) } catch {} }
  }, [isPathPharmacy, slug])

  // Target pharmacy for an apex deep-link: the authenticated user's own slug, or — when logged out —
  // the remembered hint. The latter is what makes an expired-session deep-link land on the branded
  // /p/{slug}/login (with the document preserved as return-to) rather than the marketing page.
  let apexHint: string | null = me?.pharmacy_slug ?? null
  if (!apexHint) { try { apexHint = localStorage.getItem('owlrx_pharmacy_hint') } catch { apexHint = null } }
  const apexDeepLink = !isPathPharmacy && !!apexHint
    && /^\/(secure-share|patients|provider)(\/|$)/.test(window.location.pathname)
  useEffect(() => {
    if (apexDeepLink) {
      window.location.replace(
        `/p/${apexHint}${window.location.pathname}${window.location.search}${window.location.hash}`)
    }
  }, [apexDeepLink])

  // Last-resort recovery for an OLDER apex Secure Share email opened with NO remembered pharmacy and no
  // session: resolve the pharmacy from the document id (server-side, no auth) and redirect to its
  // branded /p/{code}/secure-share?doc=… — so even the apex backlog lands on the right pharmacy login
  // → document, never the apex/SaaS login. On any failure we fall through to the normal login flow.
  let apexDocId: string | null = null
  if (!isPathPharmacy && !apexHint && /^\/secure-share(\/|$)/.test(window.location.pathname)) {
    try { apexDocId = new URLSearchParams(window.location.search).get('doc') } catch { apexDocId = null }
  }
  const [apexDocFailed, setApexDocFailed] = useState(false)
  useEffect(() => {
    if (!apexDocId) return
    let cancelled = false
    void (async () => {
      try {
        const r = await fetch(`/api/public/doc-pharmacy/${encodeURIComponent(apexDocId!)}`)
        const j = r.ok ? await r.json() : null
        if (cancelled) return
        if (j?.code) {
          window.location.replace(
            `/p/${j.code}${window.location.pathname}${window.location.search}${window.location.hash}`)
          return
        }
      } catch { /* ignore — fall through */ }
      if (!cancelled) setApexDocFailed(true)
    })()
    return () => { cancelled = true }
  }, [apexDocId])

  // Apex / admin / subdomain: nothing path-based to enforce — render through unchanged.
  if (!isPathPharmacy) {
    if (apexDeepLink || (apexDocId && !apexDocFailed)) {
      return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-mute)' }}>Loading pharmacy…</div>
    }
    return <>{children}</>
  }

  if (loading || (me?.authenticated && resolving)) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-mute)' }}>Loading pharmacy…</div>
  }
  // NEXT-1: ?next survives the hard redirects that destroy router state — a session expiry
  // mid-view logs back in to the exact tab (see safeNextPath for the open-redirect guard).
  if (!me?.authenticated) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} state={{ from: location }} replace />
  if (notFound) return <Navigate to="/login" replace />
  if (me.pharmacy_id && resolvedId && me.pharmacy_id !== resolvedId) {
    // Session belongs to a different pharmacy than the URL resolves to → force re-auth for THIS one.
    // Compared by id so it holds regardless of whether the URL used a code or a slug.
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}
